import { Button, EditableSectionContainer, EditableSectionRow, EditableSectionRowTip, Tooltip, useEditableSectionListContext, useEditableSectionsContext, useAlert } from '@uplift/ui-v2';
import { MatchingProfileRow, SeeClientsEnum } from '@uplift/types';
import { useProfileContext } from '../profile.context';
import { Box, Typography } from '@mui/material';
import { useProfileStyles } from '../styles';
import { SessionOptionsForm, ClientAgesForm, TypesOfTherapyForm, PracticeAddressForm, useProfileFormikConfigs } from '../forms';
import { IconWithContent } from '../components/IconWithContent.component';
import { Business, DeleteOutline } from '@mui/icons-material';
import { useStatesQuery } from '@uplift/queries';
import { filterOutGroupModality, getFormattedAddressParts } from '@uplift/utils';
import { PROFILE_QUERY_KEY, profileRowKeyToLabel, seeClientsTypeLabel } from '../profile.utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deletePracticeLocation } from '@uplift/api';
import { useCallback, useMemo, useState } from 'react';
import { DeleteResourceModal } from '../components/DeleteResourceModal.component';
import { RequiredProfileEditableSectionRow, RequiredProfileEditableSectionRowList } from '../components/RequiredProfileRow.component';
import { useUpBreakpoint } from '@uplift/hooks';

export const PracticeSection: React.FC = () => {
  const isSmUp = useUpBreakpoint('sm');
  const { classes } = useProfileStyles();
  const { profile } = useProfileContext();
  const { setEditKey } = useEditableSectionsContext();
  const { editIndex } = useEditableSectionListContext();
  const snackbar = useAlert();
  const queryClient = useQueryClient();
  const { getFormikConfig, getListFormikConfig } = useProfileFormikConfigs();
  const { data: states = [] } = useStatesQuery();
  const [ deletePracticeLocationId, setDeletePracticeLocationId ] = useState<string | null>(null);

  const deletePracticeMutation = useMutation({
    mutationFn: deletePracticeLocation,
    onSuccess: async () => {
      await queryClient.invalidateQueries(PROFILE_QUERY_KEY(profile.user.id));
      snackbar.success('Practice address deleted');
      setEditKey(null);
    },
    onError: async () => {
      snackbar.error('Could not delete practice address');
    },
  });

  const onCloseModal = useCallback((shouldDelete: boolean) => {
    if (shouldDelete) {
      deletePracticeMutation.mutate(deletePracticeLocationId);
    }

    setDeletePracticeLocationId(null);
  }, [ deletePracticeLocationId, deletePracticeMutation ]);

  const formikConfigs = useMemo(() => ({
    [MatchingProfileRow.practiceAddress]: getListFormikConfig(MatchingProfileRow.practiceAddress, editIndex),
    [MatchingProfileRow.sessionOption]: getFormikConfig(MatchingProfileRow.sessionOption),
    [MatchingProfileRow.supportedAgeGroups]: getFormikConfig(MatchingProfileRow.supportedAgeGroups),
    [MatchingProfileRow.modalities]: getFormikConfig(MatchingProfileRow.modalities),
  }), [ editIndex, getFormikConfig, getListFormikConfig ]);


  return (
    <>
      {deletePracticeLocationId && (
        <DeleteResourceModal
          title="Delete Practice Address?"
          onClose={onCloseModal}
          cancelButtonText="Keep address"
        />
      )}


      <EditableSectionContainer
        title="Your practice"
        rowDividers
        formikConfigs={formikConfigs}
      >
        <RequiredProfileEditableSectionRowList
          label={profileRowKeyToLabel.practiceAddress}
          rowKey={MatchingProfileRow.practiceAddress}
          contentList={profile.locations.map(({ address, id }) => {
            const addressParts = getFormattedAddressParts({
              ...address,
              address1: address.line1,
              address2: address.line2,
            },
            states.find((state) => state.id === address.stateId),
            { stateName: 'shortName' });

            return {
              content: (
                <IconWithContent
                  icon={<Business />}
                  primary={addressParts.address}
                  secondary={addressParts.addressCityState}
                />
              ),
              id: id,
            };
          })}
          form={PracticeAddressForm}
          getEditLabel={index => `Practice ${index + 1}`}

          rowFormWrapperProps={{
            cancelButtonProps: { disabled: deletePracticeMutation.isLoading },
            saveButtonProps: { disabled: deletePracticeMutation.isLoading },
          }}
          addButtonConfig={{ text: 'Add new practice' }}
          deleteButtonConfig={(id) => {
            const disabled = profile.locations.length === 1 || deletePracticeMutation.isLoading;

            const button = (
              <Button
                color="error"
                startIcon={<DeleteOutline />}
                disabled={disabled}
                onClick={() => setDeletePracticeLocationId(id)}
              >
                Delete practice
              </Button>
            );

            return isSmUp ? (
              <Tooltip placement="top" title={disabled ? 'At least 1 practice address is required' : ''}>
                <Box className={disabled && classes.cursorNotAllowed}>
                  {button}
                </Box>
              </Tooltip>
            ) : (
              <Box width="fit-content" onClick={() => disabled && snackbar.info('At least 1 practice address is required')}>
                {button}
              </Box>
            );
          }}
          editTip={(
            <EditableSectionRowTip
              title="What’s this used for?"
              description="We currently don't display addresses in your public profile: We use your address for tax purposes and to confirm where you're holding sessions."
            />
          )}
        />
        <EditableSectionRow
          label={profileRowKeyToLabel.sessionOption}
          rowKey={MatchingProfileRow.sessionOption}
          form={<SessionOptionsForm />}
          kind="editable-row"
        >
          <Typography className={classes.fontWeight500}>
            {seeClientsTypeLabel[profile.locations[0]?.practiceServiceType || SeeClientsEnum.telehealth]}
          </Typography>
        </EditableSectionRow>
        <RequiredProfileEditableSectionRow
          label={profileRowKeyToLabel.supportedAgeGroups}
          rowKey={MatchingProfileRow.supportedAgeGroups}
          form={<ClientAgesForm />}
          kind="editable-row"
        >
          <Typography className={classes.fontWeight500}>
            {profile.supportedAgeGroups.map(a => a.name).join(', ')}
          </Typography>
        </RequiredProfileEditableSectionRow>
        <RequiredProfileEditableSectionRow
          label={profileRowKeyToLabel.modalities}
          rowKey={MatchingProfileRow.modalities}
          form={<TypesOfTherapyForm />}
          kind="editable-row"
        >
          <Typography className={classes.fontWeight500}>
            {profile.modalities.filter(filterOutGroupModality).map(m => m.name).join(', ')}
          </Typography>
        </RequiredProfileEditableSectionRow>
      </EditableSectionContainer>
    </>
  );
};
